import { VesselBM } from '../../types/BusinessModel/Vessel/VesselBM';
import { VesselSlimBM } from '../../types/BusinessModel/Vessel/VesselSlimBM';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { DryDockingFactory } from './DryDockingFactory';
import { MailRecipientFactory } from './MailRecipientFactory';
import { TankFactory } from './TankFactory';
import { VesselConfigurationV3Factory } from './VesselConfigurationV3Factory';
import { TankAdjacencyConfigurationError } from 'src/types/ViewModel/Vessel/Vessel';

export class VesselFactory {
    private tankFactory = new TankFactory();
    private ddFactory = new DryDockingFactory();
    private mailRecFactory = new MailRecipientFactory();
    private vesselConfigurationV3Factory = new VesselConfigurationV3Factory();

    public getVessels(vessels: VesselBM[]) {
        if (vessels) {
            return vessels.map((o) => this.getVessel(o, true));
        }
        return [];
    }

    /** Maps slim vessel list (from ?fields=slim) — skips tank/config processing entirely. */
    public getVesselsSlim(vessels: VesselSlimBM[]): Vessel[] {
        if (!vessels) return [];
        return vessels.map((v) => new Vessel(
            v.imoNumber,
            v.vesselName ?? '???',
            v.technicalOperator ?? '???',
            0, 0, 0, 0, 0,
            null, null, null, null, null, null, null,
            v.isActive,
            null, 0, null, 0, 0, null, null, null, null, null, null, null, null, null,
            0, 0, 0, false, false, 0, null, null,
            v.fleetPerformanceAlertResponsible ?? null,
            0, 0, null, null, null, [], [],
            false, null, null, null, null,
            v.isFutureActive ?? false
        ));
    }
    public getVessel(vessel: VesselBM, shallow: boolean): Vessel {
        const cargoTanks = this.tankFactory.getCargoTankList(vessel.tanks);
        const cargoTanksById = new Map(cargoTanks.map((tank) => [tank.tankId, tank]));
        const tankAdjacencyConfigurationErrors: TankAdjacencyConfigurationError[] = [];
        cargoTanks.forEach((tank) => {
            tank.adjacentTanks = tank.adjacentTanks.filter((adjacentTankReference) => {
                const adjacentTank =
                    adjacentTankReference.adjacentTankId === null ? undefined : cargoTanksById.get(adjacentTankReference.adjacentTankId);
                if (!adjacentTank) {
                    tankAdjacencyConfigurationErrors.push({
                        tankId: tank.tankId,
                        tankName: tank.tankName,
                        adjacentTankId: adjacentTankReference.adjacentTankId,
                    });
                    console.error(
                        `Vessel ${vessel.imoNumber} tank ${tank.tankName} has an unresolved adjacent cargo tank reference (${adjacentTankReference.adjacentTankId}); adjacent-tank rules cannot evaluate this relationship.`
                    );
                    return false;
                }
                adjacentTankReference.tank = adjacentTank;
                return true;
            });
        });

        const mappedVessel = new Vessel(
            vessel.imoNumber,
            vessel.vesselName ?? '???',
            vessel.technicalOperator ?? '???',
            vessel.maxBeaufortInCalmWeather,
            vessel.deadweightSummer,
            vessel.deadweightWinter,
            vessel.deadweightTropical,
            vessel.lightShipWeight,
            vessel.vesselConfigurationV3 ? this.vesselConfigurationV3Factory.create(vessel.vesselConfigurationV3) : null,
            vessel.vesselEmail,
            vessel.suptEmail,
            vessel.opsEmail,
            vessel.orcaAolStartDate,
            vessel.orcaWebStartDate,
            shallow ? null : this.mailRecFactory.getMailRecipient(vessel.technicalSuperintendent),
            vessel.isActive,
            vessel.ownershipType,
            vessel.ownershipTypeId,
            vessel.ownershipTypeString,
            vessel.fleetId,
            vessel.vesselTradeTypeId,
            vessel.hasMaridis,
            vessel.yearBuilt,
            vessel.phoneNumber,
            vessel.hullType,
            vessel.vesselOwner,
            vessel.flag,
            vessel.callSign,
            vessel.cargoHeatingType,
            vessel.deliveredDate,
            vessel.targetSpeed,
            vessel.targetMainEngineConsumption,
            vessel.mainEngineConsumptionDeviationMargin,
            vessel.showSlip,
            vessel.showMainEngineSfoc,
            vessel.mainEngineRpmReductionFactor,
            vessel.reportsAolInId,
            vessel.vesselIsInImos,
            vessel.fleetPerformanceAlertResponsible,
            vessel.disponentOwnershipTypeId,
            vessel.financialOwnershipTypeId,
            shallow ? null : this.ddFactory.getDryDocking(vessel.nextDryDocking),
            vessel.isQuestionableCoatingCondition,
            vessel.questionableCoatingConditionComments,
            cargoTanks,
            shallow ? [] : this.tankFactory.getNonCargoTankList(vessel.tanks),
            vessel.isEquippedWithEgcs,
            vessel.hasOrcaWebSimpleMode,
            vessel.mrvVerifiedUntilDate,
            vessel.firstVoyageOrcaWebStowageViolations,
            vessel.firstVoyageOrcaWebStowageImosSync
        );
        mappedVessel.tankAdjacencyConfigurationErrors = tankAdjacencyConfigurationErrors;
        return mappedVessel;
    }
}
